import { AutoShell as AppShell } from "@/components/Nav";
import { SEO } from "@/components/SEO";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck,
  Lock,
  ServerCog,
  EyeOff,
  Scale,
  AlertTriangle,
  ArrowUpRight,
} from "lucide-react";

export default function Disclaimer() {
  return (
    <AppShell>
      <SEO
        title="Privacy & Disclaimer — no human ever reads your file"
        description="Aura's privacy pledge: no human at Aura ever reads your credit report. Everything runs through the site, automatically. See our full liability, CROA, and AI disclaimers."
        path="/privacy"
        keywords="Aura Credit privacy, credit repair privacy, credit report privacy, CROA"
      />
      <div className="mb-10">
        <div className="overline mb-3 text-[#D5FF3F]">Privacy &amp; Disclaimer</div>
        <h1 className="font-display text-5xl md:text-6xl leading-none">
          Your data.
          <span className="text-[#D5FF3F]"> Your fight.</span>
        </h1>
        <p className="text-[#A8ADA5] mt-3 max-w-2xl leading-relaxed">
          Aura Credit is software you use to fight your own credit file. Below is exactly how we
          handle your information, and the honest limits of what we can promise.
        </p>
      </div>

      {/* Privacy pillars */}
      <div className="grid md:grid-cols-2 gap-4 mb-12" data-testid="privacy-pillars">
        {[
          {
            icon: EyeOff,
            title: "No human at Aura ever reads your file.",
            body:
              "Nobody on our team opens your credit report, your negative items, your dispute letters, your SSN, or your mailing address. There is no support-agent portal, no admin dashboard that surfaces user data. Your information is processed automatically by software — end of story.",
          },
          {
            icon: ServerCog,
            title: "Everything runs through the site, automatically.",
            body:
              "Your credit report is parsed by AI in-memory (not stored to disk). Dispute letters are drafted by AI. Certified mail is dispatched through Lob's USPS API. Payments run through Stripe. All of it — every step — happens through automated, machine-to-machine calls. No human hands touch your data.",
          },
          {
            icon: Lock,
            title: "Encrypted at rest, encrypted in transit.",
            body:
              "Data in our database is stored in an isolated, access-controlled environment. Passwords are hashed with bcrypt (industry standard). Traffic between your browser and Aura is TLS 1.3. Sensitive fields on tradeline orders (SSN last-4, DOB) are only used to complete the AU-add and are never displayed back to any admin dashboard.",
          },
          {
            icon: ShieldCheck,
            title: "We don't sell your data. Full stop.",
            body:
              "Aura has no advertising business, no data-broker relationships, no analytics that fingerprint you. Third-party services we use (Anthropic Claude for AI, Stripe for payments, Lob for mail) are each bound by their own strict privacy agreements. We share only the minimum required to complete the task you asked for.",
          },
        ].map((p) => {
          const Icon = p.icon;
          return (
            <div key={p.title} className="card-surface p-6">
              <Icon size={22} className="text-[#D5FF3F] mb-4" />
              <div className="font-display text-xl mb-2 leading-tight">{p.title}</div>
              <p className="text-sm text-[#A8ADA5] leading-relaxed">{p.body}</p>
            </div>
          );
        })}
      </div>

      {/* Legal disclaimer */}
      <div className="card-surface p-8 mb-8" data-testid="legal-disclaimer">
        <div className="flex items-start gap-4 mb-6">
          <Scale size={26} className="text-[#D5FF3F] flex-shrink-0 mt-1" />
          <div>
            <h2 className="font-display text-3xl leading-tight mb-2">
              Aura is a software tool. Not a lawyer. Not a bank.
            </h2>
            <p className="text-[#A8ADA5] leading-relaxed">
              Below is the plain-English version of our disclaimer &mdash; the real, legally
              binding version lives in our Terms of Service, but the plain-English one is more
              important because it tells you the truth.
            </p>
          </div>
        </div>

        <div className="space-y-5 text-sm text-[#A8ADA5] leading-relaxed max-w-3xl">
          <div>
            <div className="text-[#F5F3EE] font-medium mb-1.5">1. Aura is not liable for outcomes.</div>
            <p>
              Aura Credit is a self-service software tool. We provide AI-drafted templates,
              automated mail dispatch, and educational content. We are <span className="text-[#F5F3EE]">not liable</span> for
              whether a specific item is removed from your credit report, how a bureau responds
              to your dispute, whether a lender approves your loan, or the accuracy of any AI-drafted
              text. You are responsible for reading every letter before it&rsquo;s sent and for verifying
              the accuracy of information you provide.
            </p>
          </div>

          <div>
            <div className="text-[#F5F3EE] font-medium mb-1.5">2. We are not a law firm.</div>
            <p>
              Nothing on Aura is legal advice. If you have complex disputes involving litigation,
              identity theft, or bankruptcy, consult a licensed consumer-law attorney. Aura may
              cite the FCRA, FDCPA, or other consumer laws in template letters &mdash; those citations
              are for educational context, not legal counsel.
            </p>
          </div>

          <div>
            <div className="text-[#F5F3EE] font-medium mb-1.5">3. We are not a credit repair organization under CROA.</div>
            <p>
              Aura is software you operate for yourself. You can dispute inaccurate information
              yourself, for free, at any time, directly with the credit bureaus. Nothing Aura does
              can be done only by us &mdash; you retain full control of every dispute, every letter,
              and every send. If you prefer to hire a licensed credit-repair company, that is your
              right.
            </p>
          </div>

          <div>
            <div className="text-[#F5F3EE] font-medium mb-1.5">4. AI can be wrong.</div>
            <p>
              Dispute letters, credit tips, and score projections are generated by large language
              models. They can make mistakes, hallucinate laws, misread your PDF, or produce
              language that doesn&rsquo;t fit your specific situation. <span className="text-[#F5F3EE]">Always read every
              draft before you sign or send.</span> Aura&rsquo;s AI is a fast typist, not an oracle.
            </p>
          </div>

          <div>
            <div className="text-[#F5F3EE] font-medium mb-1.5">5. Tradelines are not guaranteed.</div>
            <p>
              Authorized-user tradelines sold through Aura or its affiliate partners are legal but
              their effect on your credit varies by scoring model, lender, and reporting timing.
              We refund purchases that fail to post within 45 days, but we cannot promise a specific
              score change.
            </p>
          </div>

          <div>
            <div className="text-[#F5F3EE] font-medium mb-1.5">6. Certified Mail is delivered by USPS, not by Aura.</div>
            <p>
              We dispatch letters through Lob&rsquo;s Certified Mail API. Once USPS accepts the letter,
              delivery timing, tracking accuracy, and receipt of the return card are outside our
              control. We show you the same tracking data USPS provides &mdash; nothing more, nothing less.
            </p>
          </div>

          <div>
            <div className="text-[#F5F3EE] font-medium mb-1.5">7. Not FDIC insured.</div>
            <p>
              Aura is not a bank. We do not hold deposits, extend credit, or report to bureaus.
              Charges you pay to Aura are for software services and, where applicable, USPS postage
              on your behalf.
            </p>
          </div>
        </div>
      </div>

      {/* Rights */}
      <div className="card-surface p-8 mb-12">
        <div className="flex items-start gap-4 mb-4">
          <AlertTriangle size={22} className="text-[#D5FF3F] flex-shrink-0 mt-1" />
          <div>
            <h2 className="font-display text-2xl leading-tight mb-1">Your rights, in one paragraph.</h2>
            <p className="text-sm text-[#A8ADA5]">
              Under the FCRA (15 U.S.C. §1681), you have the right to a free copy of your credit
              report annually from each bureau at{" "}
              <a
                href="https://www.annualcreditreport.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#D5FF3F] underline"
              >
                annualcreditreport.com
              </a>
              . You have the right to dispute any inaccurate item directly with the bureau at no
              cost. Bureaus must investigate within 30 days. If you believe your rights have been
              violated, you may file a complaint with the CFPB at{" "}
              <a
                href="https://www.consumerfinance.gov/complaint"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#D5FF3F] underline"
              >
                consumerfinance.gov/complaint
              </a>
              .
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <Link to="/app/terms">
          <Button className="rounded-full btn-outline h-11 px-5" data-testid="disclaimer-terms-cta">
            Read full Terms of Service <ArrowUpRight size={14} className="ml-1.5" />
          </Button>
        </Link>
        <Link to="/app/guide">
          <Button className="rounded-full btn-lime h-11 px-5" data-testid="disclaimer-guide-cta">
            Free credit-building playbook <ArrowUpRight size={14} className="ml-1.5" />
          </Button>
        </Link>
        <a href="mailto:support@aura-credit.app">
          <Button className="rounded-full btn-outline h-11 px-5">Contact privacy team</Button>
        </a>
      </div>
    </AppShell>
  );
}
